import { useMemo, useState } from 'react';
import { Stack } from '@mui/material';

import { PromocodeFragment, UpdatePromocodeStatus } from '@occc/graphql';
import {
  CircularProgress,
  NO_DATA_PLACEHOLDER,
  StatusChip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@occc-app/design-system';
import { DateTimeFormat, formatDateTime, parseDate } from '@occc-app/lib/date';

import { PromocodesGlobalRowActions } from './promocodes-global-row-actions.component';

import {
  chipVariantByPublicationStatus,
  getPromocodeStatusChipVariant,
  getPromocodeStatusTitle,
  titleByPublicationStatus,
} from '../../promocodes.constants';
import { useUpdatePromocodeStatus, useDeletePromocode } from '../../hooks';
import { PromocodeStats } from '../PromocodeStats';

type PromocodesGlobalTableProps = {
  isActionsEnabled: boolean;
  promocodes: PromocodeFragment[];
  getPromocodeDetailsUrl(promocodeId: string): string;
};

export const PromocodesGlobalTable = (props: PromocodesGlobalTableProps) => {
  const { promocodes, isActionsEnabled, getPromocodeDetailsUrl } = props;
  const { update: updatePromocodeStatus, isLoading: isUpdating } = useUpdatePromocodeStatus();
  const { delete: deletePromocode, isLoading: isDeleting } = useDeletePromocode();
  const [promocodeLoadingState, setPromocodeLoadingState] = useState(new Map<string, boolean>());

  const rows = useMemo(
    () =>
      promocodes.map((promocode) => {
        const { createdBy } = promocode;
        const createdByName = createdBy.name || createdBy.email;
        const startDateString = formatDateTime(
          parseDate(promocode.startDate),
          DateTimeFormat.DateTime12h,
        );
        const endDateString = promocode.endDate
          ? formatDateTime(parseDate(promocode.endDate), DateTimeFormat.DateTime12h)
          : NO_DATA_PLACEHOLDER;

        const isStatusUpdating =
          (isUpdating || isDeleting) && promocodeLoadingState.get(promocode.id) === true;

        const handlePromocodeStatusChange = async (status: UpdatePromocodeStatus) => {
          try {
            setPromocodeLoadingState((state) => {
              state.set(promocode.id, true);
              return state;
            });
            await updatePromocodeStatus({ promocodeId: promocode.id, status });
          } catch {
            // errors handled globally
            // error snack will be shown
          } finally {
            setPromocodeLoadingState((state) => {
              state.set(promocode.id, false);
              return state;
            });
          }
        };

        const handlePromocodeDelete = async () => {
          try {
            setPromocodeLoadingState((state) => {
              state.set(promocode.id, true);
              return state;
            });
            await deletePromocode({ promocodeId: promocode.id });
          } catch {
            // errors handled globally
            // error snack will be shown
          } finally {
            setPromocodeLoadingState((state) => {
              state.set(promocode.id, false);
              return state;
            });
          }
        };

        return (
          <TableRow key={promocode.id}>
            <TableCell>{promocode.code}</TableCell>
            <TableCell>{createdByName}</TableCell>
            <TableCell>
              {isStatusUpdating ? (
                <CircularProgress size={24} />
              ) : (
                <StatusChip
                  label={getPromocodeStatusTitle(promocode.status)}
                  variant={getPromocodeStatusChipVariant(promocode.status)}
                />
              )}
            </TableCell>
            <TableCell>{startDateString}</TableCell>
            <TableCell>{endDateString}</TableCell>
            <TableCell>
              <StatusChip
                label={titleByPublicationStatus[promocode.publicationStatus]}
                variant={chipVariantByPublicationStatus[promocode.publicationStatus]}
              />
            </TableCell>
            <TableCell align='right'>
              <Stack spacing={1} direction='row' alignItems='center' justifyContent='flex-end'>
                {promocode.stats ? (
                  <PromocodeStats
                    redemptions={promocode.stats.redemptions}
                    totalOfferRedemptions={promocode.stats.totalOfferRedemptions}
                  />
                ) : (
                  <Typography variant='body2'>{NO_DATA_PLACEHOLDER}</Typography>
                )}
                {isActionsEnabled ? (
                  <PromocodesGlobalRowActions
                    promocodeName={promocode.code}
                    promocodeDetailsUrl={getPromocodeDetailsUrl(promocode.id)}
                    status={promocode.status}
                    onStatusChange={handlePromocodeStatusChange}
                    onDelete={handlePromocodeDelete}
                    isActionEnabled={!isStatusUpdating}
                    isDeleteEnabled={(promocode.stats?.redemptions ?? 0) === 0}
                  />
                ) : null}
              </Stack>
            </TableCell>
          </TableRow>
        );
      }),
    [
      promocodes,
      isUpdating,
      isDeleting,
      promocodeLoadingState,
      isActionsEnabled,
      getPromocodeDetailsUrl,
      updatePromocodeStatus,
      deletePromocode,
    ],
  );

  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Promo&nbsp;Code</TableCell>
            <TableCell>Created&nbsp;by</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Start&nbsp;Date</TableCell>
            <TableCell>End&nbsp;Date</TableCell>
            <TableCell>Publication&nbsp;Status</TableCell>
            <TableCell align='right'>Promo&nbsp;Code&nbsp;Impact</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>{rows}</TableBody>
      </Table>
    </TableContainer>
  );
};
